import React from 'react';
import { Decision, Model, Price } from "../../types/model";
import { ArrayDiff, ObjectDiff, UpdateDiff } from "../../../../utils/newDiff";
import { Grid, SvgIcon, Typography } from "@mui/material";
import ArrowIcon from '@mui/icons-material/ArrowForward';
import { toPreReq } from "../../../../utils/formatting";


type Props = {
    model: Model,
    diff: ObjectDiff<Model>
}

type UpdateProps = {
    name: string
    update: UpdateDiff<any>
}
const Update: React.FC<UpdateProps> = ({ name, update: { lhs, rhs } }) => {
    return (
        <Grid container justifyContent={'space-between'} sx={(theme) => ({ borderBottom: '1px solid #ddd', marginBottom: theme.spacing(2) })}>
            <Grid item xs={3}>
                <Typography variant={'subtitle1'}>{name}</Typography>
            </Grid>
            <Grid item xs={3} style={{ textAlign: 'right' }}>
                {rhs}
            </Grid>
            <Grid item style={{ flex: '0 0 24px' }} xs={2}>
                <SvgIcon>
                    <ArrowIcon />
                </SvgIcon>
            </Grid>
            <Grid item sx={{ color: '#952' }} xs={3}>
                <Typography variant={'subtitle2'}>{lhs}</Typography>
            </Grid>
        </Grid>
    );
}

const Add: React.FC<{ name: string, current: string | null }> = ({ name, current }) => {
    return (
        <Grid container>
            <Grid item xs={3}>
                <Typography variant={'subtitle1'}>{name}</Typography>
            </Grid>
            <Grid item xs={9} sx={{ color: '#292' }}>
                {current}
            </Grid>
        </Grid>
    )
}

const Remove: React.FC<{ name: string, previous: string | number | boolean | null }> = ({ name, previous }) => {
    return (
        <Grid container>
            <Grid item xs={3}>
                <Typography variant={'subtitle1'}>{name}</Typography>
            </Grid>
            <Grid item xs={9} sx={{ color: '#922' }}>
                {previous}
            </Grid>
        </Grid>
    )
}

export const ShowDecisionDiff: React.FC<{ diff: ObjectDiff<Decision> }> = ({ diff }) => {
    return (
        <>
            {diff.acceptLimit?.update && <Update name={'Accept Limit'} update={diff.acceptLimit.update} />}
            {diff.acceptSymbol?.update && <Update name={'Accept Symbol'} update={diff.acceptSymbol.update} />}
            {diff.denyLimit?.update && <Update name={'Deny Limit'} update={diff.denyLimit.update} />}
            {diff.denySymbol?.update && <Update name={'Deny Symbol'} update={diff.denySymbol.update} />}

            {diff.deployment?.update && <Update name={'Group Weight'} update={diff.deployment.update} />}
            {diff.deployment?.add && <Add name={'Group Weight'} current={diff.deployment.add.toString()} />}
            {diff.deployment?.remove && <Remove name={'Group Weight'} previous={diff.deployment.remove.toString()} />}

            {diff.project?.update && <Update name={'Group Weight'} update={diff.project.update} />}
            {diff.project?.add && <Add name={'Group Weight'} current={diff.project.add.toString()} />}
            {diff.project?.remove && <Remove name={'Group Weight'} previous={diff.project.remove.toString()} />}

            {diff.model?.update && <Update name={'Group Weight'} update={diff.model.update} />}
            {diff.model?.add && <Add name={'Group Weight'} current={diff.model.add.toString()} />}
            {diff.model?.remove && <Remove name={'Group Weight'} previous={diff.model.remove.toString()} />}

        </>
    )
}

export const ShowBasicDiff: React.FC<{ title: string, diff: ArrayDiff<string[]> }> = ({ title, diff }) => {
    return (
        <>
            {diff?.add && diff.add.map((x: string) => <Add key={x} name={title} current={x} />)}
            {diff?.remove && diff.remove.map((x: string) => <Remove key={x} name={title} previous={x} />)}
            {diff?.update && diff.update.map((x) => <Update key={x.lhs.toString()} name={title} update={x} />)}
        </>
    )
}

type PriceProps = {
    diff: ObjectDiff<Price>
}

export const ShowPriceDiff: React.FC<PriceProps> = ({ diff }) => {
    return (
        <>
            {diff.minAmount?.update && <Update name={'Min Amount'} update={diff.minAmount.update} />}
            {diff.maxAmount?.update && <Update name={'Max Amount'} update={diff.maxAmount.update} />}
            {diff.multiplier?.update && <Update name={'Multiplier'} update={diff.multiplier.update} />}
        </>
    )
}

const ShowDiff: React.FC<Props> = ({ model, diff }) => {


    return (
        <div style={{ marginTop: 20, width: '100%' }}>
            {diff.name?.update && <Update name={'Name'} update={diff.name.update} />}
            {diff.lowAmount?.update && <Update name={'Low Amount'} update={diff.lowAmount.update} />}
            {diff.highAmount?.update && <Update name={'Low Amount'} update={diff.highAmount.update} />}

            {diff.preReqs?.add && diff.preReqs.add.map(x => <Add key={x} name={'Pre Reqs'} current={toPreReq(x)} />)}
            {diff.preReqs?.remove && diff.preReqs.remove.map(x => <Remove key={x} name={'Pre Reqs'} previous={toPreReq(x)} />)}

            {/* {diff.stateFilters?.add && diff.stateFilters.add.map(x => <Add key={x} name={'State'} current={x} />)}
            {diff.stateFilters?.remove && diff.stateFilters.remove.map(x => <Remove key={x} name={'State'} previous={x} />)}

            {diff.originFilters?.add && diff.originFilters.add.map(x => <Add key={x} name={'Origin'} current={x} />)}
            {diff.originFilters?.remove && diff.originFilters.remove.map(x => <Remove key={x} name={'Origin'} previous={x} />)}

            {diff.campaignFilters?.add && diff.campaignFilters.add.map(x => <Add key={x} name={'Campaign'} current={x} />)}
            {diff.campaignFilters?.remove && diff.campaignFilters.remove.map(x => <Remove key={x} name={'Campaign'} previous={x} />)} */}

            {diff.groupId?.update && <Update name={'GroupID'} update={diff.groupId.update} />}
            {diff.groupId?.add && <Add name={'GroupID'} current={diff.groupId.add.toString()} />}
            {diff.groupId?.remove && <Remove name={'GroupID'} previous={diff.groupId.remove} />}

            {diff.groupWeight?.update && <Update name={'Group Weight'} update={diff.groupWeight.update} />}
            {diff.groupWeight?.add && <Add name={'Group Weight'} current={diff.groupWeight.add.toString()} />}
            {diff.groupWeight?.remove && <Remove name={'Group Weight'} previous={model.groupWeight} />}
        </div>
    )
}

export default ShowDiff;